import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useSelector, useDispatch } from "react-redux";
import { fetchDashboardData } from "../../redux/slices/dashboardSlice";
import { IconBellRinging } from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import ErrorFallback  from "../../components/common/ErrorFallback";
import LoadingFallback from "../../components/common/LoadingFallback";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);


function StatsCard({ title, value, change }) {
  const changeColor = change >= 0 ? "text-green-500" : "text-red-500";
  const changeSymbol = change >= 0 ? "+" : "";

  return (
    <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] flex flex-col items-center justify-center py-6">
      <h4 className="text-[#120E43] font-semibold">{title}</h4>
      <p className="text-[#120E43] text-2xl font-bold">{value}</p>
      {change !== undefined && (
        <p className={`text-sm ${changeColor}`}>
          {changeSymbol}{change}%
        </p>
      )}
    </div>
  );
}

function ActivityItem({ icon, text }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-lg">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function Dashboard() {
  const dispatch = useDispatch();
   const { setLayoutConfig } = useAdminLayout();
  const {
    loading,
    error,
    stats,
    salesTrends,
    bestSellers,
    customerGrowth
  } = useSelector((state) => state.dashboard);

  useEffect(() => {
    setLayoutConfig({
      title: "Dashboard",
      showBackButton: false,
      scrollable: true,
    });

    dispatch(fetchDashboardData());
  }, [dispatch, setLayoutConfig]);

  // Chart data
  const lineData = {
    labels: salesTrends?.map(item => item.month) || [],
    datasets: [
      {
        label: "Sales",
        data: salesTrends?.map(item => item.amount) || [],
        borderColor: "#39C16C",
        backgroundColor: "rgba(57, 193, 108, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const barData = {
    labels: bestSellers?.map(item => item.name) || [],
    datasets: [
      {
        label: "Sales",
        data: bestSellers?.map(item => item.amount) || [],
        backgroundColor: ["#39C16C", "#FFD500", "#FF0000"],
      },
    ],
  };

  const doughnutData = {
    labels: [
      `New Customers (${customerGrowth?.newPercent || 0}%)`,
      `Returning (${customerGrowth?.returningPercent || 0}%)`,
    ],
    datasets: [
      {
        data: customerGrowth
          ? [customerGrowth.newPercent, customerGrowth.returningPercent]
          : [0, 0],
        backgroundColor: ["#39C16C", "#FFD500"],
        hoverBackgroundColor: ["#39C16C", "#FFD500"],
        borderWidth: 0,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${Number(value).toLocaleString()}`,
        },
      },
    },
  };

  const barOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
      y: {
        grid: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.formattedValue}%`,
        },
      },
    },
  };

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(fetchDashboardData())} />;
  }

  return (

    // <AdminLayout title="Dashboard" showBackButton={false}>

      <main className="p-6 flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatsCard
            title="Total Orders"
            value={stats?.totalOrders?.toLocaleString()}
            change={stats?.totalOrdersChange}
          />
          <StatsCard
            title="Revenue"
            value={`$${stats?.revenue?.toLocaleString()}`}
            change={stats?.revenueChange}
          />
          <StatsCard
            title="Pending"
            value={stats?.pending}
            change={stats?.pendingChange}
          />
          <StatsCard
            title="Customers"
            value={stats?.customers?.toLocaleString()}
          />
          <StatsCard
            title="Low Stock"
            value={stats?.lowStock}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] p-4">
            <h3 className="text-[#120E43] font-semibold mb-2">Recent Activities</h3>
            <hr className="border-[#463BC9]/40 mb-3" />
            <div className="space-y-2 text-sm max-h-40 overflow-auto pr-2">
              <ActivityItem
                icon="✅"
                text="New Order: Order #12345 Placed (10 Min Ago)"
              />
              <ActivityItem
                icon="💰"
                text="Payment Received: $250 (1 Hour Ago)"
              />
              <ActivityItem
                icon="📦"
                text="Stock Updated: 5 New Items Added (2 Hours Ago)"
              />
            </div>
          </div>

          <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] p-4">
            <h3 className="text-[#120E43] font-semibold mb-2">Sales Trends</h3>
            <hr className="border-[#463BC9]/40 mb-3" />
            <div className="w-full h-64">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] p-4">
            <h3 className="text-[#120E43] font-semibold mb-2">Best Selling Products</h3>
            <hr className="border-[#463BC9]/40 mb-3" />
            <div className="w-full h-52">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>

          <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] p-4">
            <h3 className="text-[#120E43] font-semibold mb-2">Customer Growth</h3>
            <hr className="border-[#463BC9]/40 mb-3" />
            <div className="w-full h-52 flex items-center justify-center">
              <div className="w-40 h-40">
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>
            </div>
            <div className="mt-4 text-sm space-y-1">
              <p>
                <span className="inline-block w-3 h-3 rounded-full mr-2 bg-[#39C16C]" />
                {customerGrowth?.newPercent || 0}% New Customers
              </p>
              <p>
                <span className="inline-block w-3 h-3 rounded-full mr-2 bg-[#FFD500]" />
                {customerGrowth?.returningPercent || 0}% Returning
              </p>
            </div>
          </div>
        </div>
      </main>
    // </AdminLayout>
  );
}

export default Dashboard;



